import axios from "axios";

/**
 * Payload-parity harness for the reports picker migration (flat → cascade).
 *
 * Goal: prove that swapping the flat `SearchableLightbox` picker for the new
 * `CascadeFieldPicker` produces BYTE-IDENTICAL stored widget config and
 * BYTE-IDENTICAL `/api/insightMatrix/*` request bodies.
 *
 * Entirely inert unless `localStorage.OOLOI_PICKER_PARITY` is set (any truthy
 * string). No-ops in production for every user who hasn't opted in.
 *
 * Usage (dev console):
 *   localStorage.OOLOI_PICKER_PARITY = '1'   // then reload
 *   // ...perform an identical pick sequence in a widget...
 *   copy(JSON.stringify(window.__pickerParityLog, null, 2))   // export
 *   __clearParityLog()                                        // reset between runs
 *
 * Procedure to compare flat vs cascade for a slot:
 *   1. On `main` (flat picker): pick e.g. dataset "Safety Concern" → field
 *      "Reason", export the log.
 *   2. On the feature branch (cascade): repeat the exact same pick, export.
 *   3. diff the two JSON dumps (ignoring `ts`). Config snapshots + request
 *      bodies must match exactly.
 */

const STORAGE_KEY = "OOLOI_PICKER_PARITY";
const AGGR_URL_MATCH = "/api/insightMatrix/";

let interceptorRegistered = false;

export const isParityEnabled = () => {
  try {
    return !!window.localStorage?.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
};

const ensureLog = () => {
  if (!window.__pickerParityLog) window.__pickerParityLog = [];
  return window.__pickerParityLog;
};

const push = (entry) => {
  // ts is wall-clock only for human ordering; diffing ignores it.
  ensureLog().push({ ts: new Date().toISOString(), ...entry });
};

/**
 * Append a snapshot of the full report config array (deep-cloned) whenever it
 * changes. Call from a useEffect on `config`.
 */
export const logConfigSnapshot = (config) => {
  if (!isParityEnabled()) return;
  try {
    push({ kind: "config", config: JSON.parse(JSON.stringify(config)) });
  } catch (e) {
    push({ kind: "config", error: String(e) });
  }
};

/**
 * Register a global axios request interceptor (once) that records the body of
 * every POST to `/api/insightMatrix/*`. Idempotent — safe to call on every
 * mount. The interceptor itself is cheap and re-checks `isParityEnabled()` per
 * request, so toggling the flag at runtime works without re-registering.
 */
export const initParityLogging = () => {
  if (interceptorRegistered) return;
  interceptorRegistered = true;

  axios.interceptors.request.use((cfg) => {
    try {
      if (
        isParityEnabled() &&
        (cfg.method || "").toLowerCase() === "post" &&
        typeof cfg.url === "string" &&
        cfg.url.includes(AGGR_URL_MATCH)
      ) {
        const body =
          typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data;
        push({ kind: "request", url: cfg.url, body });
      }
    } catch {
      /* never let logging break a request */
    }
    return cfg;
  });

  if (typeof window !== "undefined") {
    window.__clearParityLog = () => {
      window.__pickerParityLog = [];
    };
  }
};
